import React, { useEffect, useState } from 'react';

import { connect } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import AppBar from '@material-ui/core/AppBar';
import Tab from '@material-ui/core/Tab';
import Tabs from '@material-ui/core/Tabs';

import { loadEmployeeComments } from '../../comments/actions/load-comments';
import { commentsForIdSelector } from '../../comments/comment-selectors';
import { loadContactFiles } from '../../contact-file/actions/load-contact-files';
import { contactFilesForContactIdSelector } from '../../contact-file/contact-file-selectors';
import { loadContactMessages } from '../../contacts/actions/load-contact-messages';
import { messagesWithImagesForContactIdSelector } from '../../reducers/contact-selectors';
import { userSelector } from '../../reducers/current-user-selectors';

import { EmployeeCommentList } from '../../comments/components/employee-comment-list';
import { ContactFiles } from '../../contact-file/contact-files';
import { UiIconTabLabel } from '../../ui/ui-icon-tab';
import { UiTabPanel } from '../../ui/ui-tab-panel';
import { EmployeeHistory } from '../employee-history';

import { EmployeeDetails } from './employee-details';

import { MaterialDesignIcon, PROFILE_TAB_ICON_NAMES } from '../../util/material-design-icons';
import { EMPLOYEE_PERMISSION } from '../../util/permissions';
import { LoadingMessage } from '../../widgets/loading-message';

const mapStateToProps = (state: any, { employee: { employeeId, contactId } }: InputProps) => {
  const contactFiles = contactFilesForContactIdSelector(state, contactId) || [];
  const messagesWithImages = messagesWithImagesForContactIdSelector(state, contactId) || [];

  return {
    user: userSelector(state),
    numComments: commentsForIdSelector(state, employeeId, 'employee')?.length,
    numDocuments: contactFiles.length + messagesWithImages.length,
  };
};

const mapDispatchToProps = (
  dispatch: ThunkDispatch<any, any, AnyAction>,
  { employee: { employeeId, contactId } }: InputProps,
) => ({
  actions: {
    loadEmployeeComments: () => dispatch(loadEmployeeComments({ employeeId })),
    loadContactFiles: () => dispatch(loadContactFiles(contactId)),
    loadContactMessages: () => dispatch(loadContactMessages(contactId)),
  },
});

interface InputProps {
  employee: Employee;
  tab?: string | null;
}

type ReduxProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = InputProps & ReduxProps & DispatchProps;

const EMPLOYEE_PROFILE_TABS: Record<string, number> = {
  DETAILS: 0,
  COMMENTS: 1,
  DOCUMENTS: 2,
  HISTORY: 3,
};

export const EmployeeProfileView = connect(
  mapStateToProps,
  mapDispatchToProps,
)(({ employee, user, numComments, numDocuments, actions, tab }: Props) => {
  const hasAllEmployeesPermission = user.employeesPermission === EMPLOYEE_PERMISSION.ALL;
  const [currentSelectedTab, setCurrentSelectedTab] = useState<number>(0);

  useEffect(() => {
    actions.loadEmployeeComments();
    actions.loadContactFiles();
    actions.loadContactMessages();
  });

  useEffect(() => {
    if (tab) {
      setCurrentSelectedTab(EMPLOYEE_PROFILE_TABS[tab.toUpperCase()] ?? 0);
    }
  }, [tab]);

  const handleTabChange = (_e: React.ChangeEvent<{}>, selectedTab: number) => {
    setCurrentSelectedTab(selectedTab);
  };

  if (!employee) {
    return <LoadingMessage />;
  }

  return (
    <div>
      <AppBar position="static">
        <Tabs value={currentSelectedTab} onChange={handleTabChange}>
          <Tab
            label={
              <UiIconTabLabel
                label="Details"
                Icon={<MaterialDesignIcon iconName={PROFILE_TAB_ICON_NAMES.DETAILS} />}
              />
            }
          />
          <Tab
            label={
              <UiIconTabLabel
                label="Notes"
                Icon={<MaterialDesignIcon iconName={PROFILE_TAB_ICON_NAMES.COMMENTS} />}
                count={numComments}
              />
            }
          />
          <Tab
            label={
              <UiIconTabLabel
                label="Documents"
                Icon={<MaterialDesignIcon iconName={PROFILE_TAB_ICON_NAMES.DOCUMENTS} />}
                count={numDocuments}
              />
            }
          />
          {hasAllEmployeesPermission && (
            <Tab
              label={
                <UiIconTabLabel
                  label="History"
                  Icon={<MaterialDesignIcon iconName={PROFILE_TAB_ICON_NAMES.HISTORY} />}
                />
              }
            />
          )}
        </Tabs>
      </AppBar>
      <div className="card card-body">
        <UiTabPanel value={currentSelectedTab} index={EMPLOYEE_PROFILE_TABS.DETAILS}>
          <EmployeeDetails employee={employee} />
        </UiTabPanel>
        <UiTabPanel value={currentSelectedTab} index={EMPLOYEE_PROFILE_TABS.COMMENTS}>
          <EmployeeCommentList employeeId={employee.employeeId} orgId={employee.orgId} />
        </UiTabPanel>
        <UiTabPanel value={currentSelectedTab} index={EMPLOYEE_PROFILE_TABS.DOCUMENTS}>
          <ContactFiles
            user={user}
            contactId={employee.contactId}
            orgId={employee.orgId}
            employeeId={employee.employeeId}
          />
        </UiTabPanel>
        <UiTabPanel value={currentSelectedTab} index={EMPLOYEE_PROFILE_TABS.HISTORY}>
          <EmployeeHistory employee={employee} />
        </UiTabPanel>
      </div>
    </div>
  );
});
